<template>
  <div class="survey-response-detail">
    <!-- if loading, show LoadSpinner -->
    <LoadSpinner v-if="loading" style="padding: 2rem 0" />
    <!-- else, show page-content -->
    <div v-else class="page-content">
      <!-- if statusCodeError show notification, for either 404 or 500 -->
      <div class="error-container" v-if="errorCode">
        <!-- 404 notification-->
        <div class="notification-card" v-if="errorCode === 404">
          <h2>404</h2>
          <p v-translate>Survey Response Not Found.</p>
        </div>
        <!-- 500 notification-->
        <div class="notification-card" v-else>
          <h2>500</h2>
          <p v-translate>Something went wrong, please try again later.</p>
        </div>
      </div>
      <!-- once surveyResponse is present, work with it to show appropriate UI-->
      <div class="survey-response" v-else-if="surveyResponse">
        <!-- if surveyResponse already complete, show notification only -->
        <div class="notification-card" v-if="surveyResponse.isComplete">
          <h2 v-translate>Thank You!</h2>
          <p v-translate>Survey has been completed.</p>
        </div>
        <!-- else if surveyResponse isComplete was not provided, show notification only -->
        <div class="notification-card" v-else-if="surveyResponse.isComplete === null">
          <h2>500</h2>
          <p v-translate>Something went wrong, please try again later.</p>
        </div>
        <!-- else, show the surveyResponse and let the user complete it -->
        <div v-else>
          <div class="notification-card">
            <h2 v-translate>Welcome back!</h2>
            <p>
              {{
                $language.current
                  | translateToCurrentLang(
                    surveyResponse.instructionsEs,
                    surveyResponse.instructionsEn,
                  )
              }}
            </p>
          </div>
          <SurveyResponseComponent @survey-done="onSurveyDone" :surveyResponse="surveyResponse" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// components
import { LoadSpinner } from '@/components'
import SurveyResponseComponent from '@/components/SurveyResponse'
// service models
import SurveyResponseModel from '@/services/surveyresponses'
import { textToKabobCase } from '@/services/utils'
import { analyticsMixin } from '@/services/googleAnalytics'
import { mapState } from 'vuex'

export default {
  name: 'SurveyResponseDetail',
  mixins: [analyticsMixin],
  components: {
    LoadSpinner,
    SurveyResponseComponent,
  },
  data() {
    return {
      surveyResponse: null, // the whole enchilada, with surveyRef
      errorCode: null, // responseStatusCode
      loading: false, // initial page load
    }
  },
  created() {
    this.loading = true
    this.getSurveyResponse().finally(() => {
      this.loading = false
    })
  },
  methods: {
    getSurveyResponse() {
      const surveyResponsePromise = SurveyResponseModel.api
        .retrieve(this.$route.params.id)
        .then((surveyResponse) => {
          this.surveyResponse = surveyResponse
          let pageTitle = this.$route.name
          let pagePath = '/survey-responses/' + textToKabobCase(this.surveyResponse.surveyRef.name)
          this.trackCustomPageview(pageTitle, pagePath)
        })
        .catch((error) => {
          try {
            error.response.status === 404 ? (this.errorCode = 404) : (this.errorCode = 500)
          } catch {
            this.errorCode = 500
          }
        })
      return surveyResponsePromise
    },
    onSurveyDone(result) {
      if (result.success) {
        this.$Alert.alert({
          type: 'success',
          message: `<h4>${this.$gettext('Success!')}</h4><p>${this.$gettext(
            'Survey response submitted!',
          )}</p>`,
          timeout: 6000,
        })
        this.$router.push({ name: 'Home' })
      } else {
        this.$Alert.alert({
          type: 'error',
          message: `<h4>Something went wrong!</h4><p>Please try again later.</p>`,
          timeout: 6000,
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/mixins/containers';

.survey-response-detail {
  @include screen-container();
}

.page-content {
  display: block;
  box-sizing: border-box;
}

.notification-card {
  width: 80%;
  border-radius: 9px;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  align-items: center;
  margin: 1rem auto;
  padding: 30px 15px;
  box-shadow: 0 4px 8px 0 $mid-gray;
  background-color: var(--light-brand-color);
}
</style>
